"use client";

import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
  DropdownLabel,
} from "./catalyst/dropdown";
import {
  CalendarIcon,
  UserIcon,
  CogIcon,
  ArrowLeftStartOnRectangleIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/solid";
import { usePathname, useRouter } from "next/navigation";
import { getCreatorForCurrentUser } from "@/app/actions";
import { useEffect, useState, useCallback } from "react";
import { useSession, signOut } from "@/lib/auth/client";
import { isProtectedAccess } from "@/lib/utils/routes";
import { AvatarButton } from "./catalyst/avatar";
import { useTranslations } from "next-intl";
import { Button } from "./catalyst/button";
import { Creator } from "@/lib/entities";
import Link from "next/link";

export function ProfileButton() {
  const t = useTranslations("ProfileButton");
  const { data: session } = useSession();
  const [creator, setCreator] = useState<Creator | undefined>();
  const [isClient, setIsClient] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsClient(true);
    const fetchCreator = async () => {
      const creator = await getCreatorForCurrentUser();
      setCreator(creator);
    };
    fetchCreator();
  }, []);

  const handleSignOut = useCallback(() => {
    const currentPath = pathname || "/";
    signOut({
      fetchOptions: {
        onSuccess: () => {
          if (isProtectedAccess(currentPath)) {
            router.replace("/");
            return;
          }

          router.refresh();
        },
        onError: () => {
          router.refresh();
        },
      },
    });
  }, [pathname, router]);

  // During SSR and initial hydration, always render the same fallback
  // to prevent hydration mismatch
  if (!isClient || !session) {
    // Used prefetch={false} to avoid caching the link for sign-in
    return (
      <Link href="/creator" prefetch={false}>
        <Button
          color="dark/white"
          aria-label={t("hostEvent")}
          data-testid="host-event-button"
        >
          <CalendarIcon className="size-4" />
          <span className="hidden sm:block">{t("hostEvent")}</span>
        </Button>
      </Link>
    );
  }

  return (
    <Dropdown>
      <DropdownButton
        as={AvatarButton}
        src={session.user.image || undefined}
        initials={session.user.name?.charAt(0) || "U"}
        className="size-8 cursor-pointer"
        size={32}
        aria-label={t("accountOptions")}
        data-testid="profile-menu-button"
      />
      <DropdownMenu anchor="bottom">
        {creator ? (
          <>
            <DropdownItem href={`/creator/${creator.slug}`}>
              <UserIcon />
              <DropdownLabel>{t("manageProfile")}</DropdownLabel>
            </DropdownItem>
          </>
        ) : (
          <DropdownItem href="/onboarding">
            <VideoCameraIcon />
            <DropdownLabel>{t("becomeCreator")}</DropdownLabel>
          </DropdownItem>
        )}
        <DropdownItem href="/account">
          <CogIcon />
          <DropdownLabel>{t("accountSettings")}</DropdownLabel>
        </DropdownItem>
        <DropdownDivider />
        <DropdownItem onClick={handleSignOut} data-testid="profile-sign-out">
          <ArrowLeftStartOnRectangleIcon />
          <DropdownLabel>{t("signOut")}</DropdownLabel>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}
